import { useEffect, useMemo, useState } from "react";

import { useFormContext } from "react-hook-form";

import {
  abortPreviousRequestsIfAny,
  getServiceabilityBuildingUrlKey,
  getSolarBatteryUrlKey,
} from "@/abort";
import { IISolarBatteryOutlookResponse } from "@/contract/generated/electrify-v1";
import { useServiceability } from "@/features/SolarForm/useServiceability";
import { useRedirect } from "@/hooks/useRedirect";
import { useSolarBattery } from "./useSolarBattery";
import { getMedianSizeIndex } from "./utils";
import { useQuote } from "../SolarOutlook/useQuote";

type useSolarOutlookProps = {
  hardStopRoute: () => Promise<string>;
};

export const useSolarBatteryOutlook = ({
  hardStopRoute,
}: useSolarOutlookProps) => {
  const { goNextRoute } = useRedirect();
  const { getValues, setValue } = useFormContext();
  const { checkBuildingStructure } = useServiceability();
  const [loading, setLoading] = useState(false);
  const [solarBatteryOutlook, setSolarBatteryOutlook] = useState<
    Array<IISolarBatteryOutlookResponse>
  >([]);

  const { getQuote } = useQuote();
  const { getSolarBattery } = useSolarBattery();

  const navigateHardStop = async (error?: Error) => {
    if (error?.message === "ROOF_NOT_ENOUGH_FOR_RECOMMENDATION") {
      setValue("hardStop", "solarOutlookRoofSize");
    } else {
      setValue("hardStop", "recommendation");
    }

    const to = await hardStopRoute();
    goNextRoute(to);
  };

  const doCheck = async () => {
    setLoading(true);
    const storeys = Number.parseInt(
      getValues("userStoreys") ?? getValues("accurassiStoreys"),
      10
    );
    const roofMaterial = getValues("roofMaterial");
    const result = await checkBuildingStructure(roofMaterial, storeys);

    if (result && result.error) {
      setLoading(false);
      await navigateHardStop();

      return;
    }

    const { error: quoteError, result: quoteResult } = await getQuote();

    if (quoteError || !quoteResult) {
      setLoading(false);
      await navigateHardStop(quoteError);
      return;
    }

    const { error, result: solarBtteryResult } = await getSolarBattery();

    if (error || !solarBtteryResult) {
      setLoading(false);
      await navigateHardStop(error);
      return;
    }

    setLoading(false);
    setSolarBatteryOutlook(solarBtteryResult);
  };

  useEffect(() => {
    doCheck();

    return () => {
      // clean up
      abortPreviousRequestsIfAny(
        getServiceabilityBuildingUrlKey(),
        getSolarBatteryUrlKey()
      );
    };
  }, []);

  const medianSizeIndex = useMemo(() => {
    return getMedianSizeIndex({ solarBatteryOutlook });
  }, [solarBatteryOutlook]);

  return {
    loading,
    navigateHardStop,
    solarBatteryOutlook,
    medianSizeIndex,
  };
};
